import React, { useEffect, useState } from "react";
import axios from "axios";
import styled, { createGlobalStyle } from "styled-components";
import { QRCodeCanvas } from "qrcode.react";
import { Helmet } from "react-helmet";

const GlobalStyle = createGlobalStyle`
  html, body, #root {
    margin: 0;
    padding: 0;
    width: 100%;
    min-height: 100%;
  }

  body {
    background: #101827;
  }
`;

const Screen = styled.main`
  position: fixed;
  inset: 0;
  overflow: auto;
  background: #101827;
  color: white;
  font-family: Arial, sans-serif;

  &,
  & * {
    box-sizing: border-box;
  }
`;

const GymLogoBox = styled.div`
  position: relative;
  z-index: 2;
  width: 150px;
  height: 82px;
  margin: 4vh 0 0 6vw;
  padding: 6px 8px;

  display: flex;
  align-items: center;
  justify-content: center;

  background: black;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
`;

const GymLogo = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  background: black;
`;

const Background = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    ${({ $rtl }) => ($rtl ? "270deg" : "90deg")},
    rgba(5, 12, 24, 0.94) 0%,
    rgba(5, 12, 24, 0.76) 45%,
    rgba(5, 12, 24, 0.28) 100%
  );
`;

const Layout = styled.div`
  position: relative;
  z-index: 1;
  min-height: calc(100vh - 82px - 4vh);
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 5vw;
  padding: 6vh 6vw;

  @media (max-aspect-ratio: 1/1) {
    grid-template-columns: 1fr;
    align-content: center;
    gap: 4vh;
    padding: 5vh 7vw;
  }
`;

const Content = styled.div`
  min-width: 0;
  text-align: ${({ dir }) => (dir === "rtl" ? "right" : "left")};
`;

const Label = styled.div`
  color: #65bdff;
  font-size: clamp(50px, 2vw, 36px);
  font-weight: 800;
  margin-bottom: 3vh;
`;

const Title = styled.h1`
  margin: 0 0 3vh;
  color: white;
  font-size: clamp(32px, 5vw, 96px);
  line-height: 1.08;
  font-weight: 900;
  overflow-wrap: anywhere;
  text-shadow: 0 3px 24px rgba(0, 0, 0, 0.4);
`;

const Description = styled.p`
  margin: 0;
  max-width: 850px;
  color: rgba(255, 255, 255, 0.9);
  font-size: clamp(18px, 2vw, 36px);
  line-height: 1.5;
  white-space: pre-line;

  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const JoinBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  text-align: center;
`;

const QRCard = styled.a`
  display: block;
  width: clamp(190px, 23vw, 420px);
  padding: 20px;
  background: white;
  border-radius: 24px;
  color: #101827;
  text-decoration: none;
  box-shadow: 0 15px 60px rgba(0, 0, 0, 0.35);

  &:hover {
    color: #101827;
    text-decoration: none;
  }

  canvas {
    display: block;
    width: 100% !important;
    height: auto !important;
  }

  @media (max-aspect-ratio: 1/1) {
    width: min(52vw, 32vh);
  }
`;

const ScanText = styled.div`
  margin-top: 14px;
  font-size: clamp(18px, 1.7vw, 30px);
  font-weight: 900;
`;

const Code = styled.div`
  color: white;
  font-size: clamp(16px, 1.4vw, 26px);

  strong {
    display: block;
    margin-top: 6px;
    font-size: clamp(22px, 2vw, 36px);
    overflow-wrap: anywhere;
  }
`;

const PoweredBy = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: clamp(14px, 1.1vw, 20px);
  direction: ltr;

  strong {
    color: white;
  }
`;

const Status = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 30px;
  text-align: center;
  font-size: 26px;
`;

const RetryButton = styled.button`
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  background: #007df4;
  color: white;
  font-size: 18px;
  cursor: pointer;
`;

function resolveImage(path) {
  if (!path) return "/images/prebealyoshare.png";

  if (/^https?:\/\//i.test(path)) return path;

  const base = process.env.REACT_APP_IMG_FOLDER || "";

  return `${base.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
}

export default function ChallengeTV({ match }) {
  const challengeId = match.params.id;

  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [imageFailed, setImageFailed] = useState(false);
  const [gymBrand, setGymBrand] = useState(null);

  const circleToken = new URLSearchParams(window.location.search).get(
    "circleToken",
  );

  useEffect(() => {
    let active = true;

    setLoading(true);
    setError("");
    setChallenge(null);
    setImageFailed(false);

    axios
      .get(
        process.env.REACT_APP_API_URL +
          "goals/" +
          encodeURIComponent(challengeId),
        { timeout: 20000 },
      )
      .then(({ data }) => {
        if (!active) return;

        if (!data || !data._id || !data.goal_title) {
          throw new Error("Invalid challenge response");
        }

        setChallenge({
          id: data._id,
          title: data.goal_title,
          description: data.goal_desc || "",
          imageUrl: resolveImage(data.goal_imageurl),
          inviteId: data.goal_inviteid || "",
        });
      })
      .catch(() => {
        if (active) {
          setError("Unable to load this challenge. Please try again.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [challengeId, attempt]);

  const rtlParam = new URLSearchParams(window.location.search).get("rtl");

  const rtl =
    rtlParam === "1"
      ? true
      : rtlParam === "0"
        ? false
        : Boolean(
            challenge &&
            /[\u0590-\u05FF]/.test(challenge.title + challenge.description),
          );

  useEffect(() => {
    let active = true;

    setGymBrand(null);

    if (!circleToken) return;

    axios
      .get(
        process.env.REACT_APP_API_URL +
          "circles/leaderboard/public/" +
          encodeURIComponent(circleToken),
        { timeout: 20000 },
      )
      .then(({ data }) => {
        if (!active) return;

        const circle = data?.circle;

        if (circle?.circle_logo) {
          setGymBrand({
            name: circle.name || "Gym",
            logo: resolveImage(circle.circle_logo),
          });
        }
      })
      .catch((error) => {
        console.error("Unable to load gym logo:", error);
      });

    return () => {
      active = false;
    };
  }, [circleToken]);

  const joinUrl = challenge
    ? `https://www.bealyo.com/challenge/${encodeURIComponent(challenge.id)}`
    : "";

  return (
    <Screen>
      <GlobalStyle />

      <Helmet>
        <title>{challenge ? challenge.title : "Challenge TV"} | Bealyo</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      {loading ? (
        <Status>Loading challenge...</Status>
      ) : error ? (
        <Status>
          <div>{error}</div>

          <RetryButton
            type="button"
            onClick={() => setAttempt((value) => value + 1)}
          >
            Try again
          </RetryButton>
        </Status>
      ) : challenge ? (
        <>
          {!imageFailed && (
            <Background
              src={challenge.imageUrl}
              alt=""
              onError={() => setImageFailed(true)}
            />
          )}

          <Overlay $rtl={rtl} />

          {gymBrand && (
            <GymLogoBox>
              <GymLogo
                src={gymBrand.logo}
                alt={`${gymBrand.name} logo`}
                onError={() => setGymBrand(null)}
              />
            </GymLogoBox>
          )}

          <Layout dir={rtl ? "rtl" : "ltr"}>
            <Content dir={rtl ? "rtl" : "ltr"}>
              <Label>
                {rtl ? "האתגר הבא שלכם מתחיל כאן" : "YOUR NEXT CHALLENGE"}
              </Label>

              <Title>{challenge.title}</Title>

              <Description>{challenge.description}</Description>
            </Content>

            <JoinBlock>
              <QRCard
                href={joinUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={rtl ? "פתיחת האתגר" : "Open challenge"}
              >
                <QRCodeCanvas
                  value={joinUrl}
                  size={512}
                  level="M"
                  bgColor="#ffffff"
                  fgColor="#101827"
                  includeMargin={true}
                />

                <ScanText>{rtl ? "סרקו והצטרפו " : "Scan to join"}</ScanText>
              </QRCard>

              {!!challenge.inviteId && (
                <Code dir={rtl ? "rtl" : "ltr"}>
                  {rtl ? "קוד האתגר " : "Challenge code "}

                  <strong dir="ltr">{challenge.inviteId}</strong>
                </Code>
              )}

              <PoweredBy>
                Powered by <strong>Bealyo</strong>
              </PoweredBy>
            </JoinBlock>
          </Layout>
        </>
      ) : null}
    </Screen>
  );
}
